/*
 * ANANDA Hero — drag-to-rotate interactive pouch with live price update.
 * Each img-link carries its product's price in data-price; apply() reads
 * the nearest active link's data-price and writes it to .ananda-hero__price
 * so the price visible under the pouch tracks whichever flavor is shown.
 *
 * Auto-rotate loop:
 *   On load (and only if the user hasn't asked for reduced motion) we kick
 *   off a 1.5-second cadence that cycles through the flavors so the hero
 *   feels alive even before anyone interacts.
 *
 *   Stop semantics:
 *     - Pill click  → stopAutoLoopForGood(): the user picked a flavor,
 *       they're done watching the carousel. Never restart.
 *     - Drag / touch on the pouch → pauseAutoLoop() during the gesture,
 *       startAutoLoop() on release. The pause is just so the autoTick
 *       doesn't yank the pouch out of the user's hand mid-drag; once
 *       they let go, the carousel keeps going from wherever it landed.
 *       (Earlier we used to stop forever on any pouch touch — the user
 *       wanted only pill clicks to be the "I'm taking over" signal.)
 *     - Tab hidden → pauseAutoLoop(), resume on visibilitychange.
 *
 *   Forward-wrap: when going from the last flavor (index 3) back to the
 *   first (index 0), we animate to target = current + 1 (e.g. 3 → 4) and
 *   let apply()'s internal modulo render that as 3 → 0 in the forward
 *   direction. Animating directly 3 → 0 would lerp backwards through
 *   2 and 1, which reads as a quick reverse rewind — ugly. After the
 *   tween settles we normalize current back into [0, COUNT) so it doesn't
 *   drift unbounded across many cycles.
 *
 *   Click vs. drag (desktop fix): pointer capture is engaged only AFTER the
 *   pointer moves past the drag threshold — never on a plain click. On a
 *   mouse, capturing on pointerdown makes the browser retarget the follow-up
 *   click to the stage instead of the product <a>, so the link never
 *   navigates. Touch hit-tests the click normally, which is why the bug was
 *   desktop-only.
 */
(function () {
  function init() {
    var hero  = document.querySelector('[data-ananda-hero]');
    if (!hero || hero.dataset.anandaHeroInit === '1') return;
    hero.dataset.anandaHeroInit = '1';

    var stage    = hero.querySelector('[data-ananda-hero-stage]');
    var imgs     = hero.querySelectorAll('.ananda-hero__img');
    var links    = hero.querySelectorAll('.ananda-hero__img-link');
    var pills    = hero.querySelectorAll('[data-ananda-hero-pills] .ananda-hero__pill');
    var hint     = hero.querySelector('[data-ananda-hero-hint]');
    var priceEl  = hero.querySelector('[data-ananda-hero-price]');
    if (!stage || imgs.length === 0) return;

    var COLORS = ['#E37A2C', '#1F6E4F', '#B33640', '#1E5A99'];
    var COUNT  = Math.min(imgs.length, COLORS.length);

    var current = 0;
    var dragging = false;
    var startX = 0;
    var startProgress = 0;
    var moved = 0;
    var captured = false;
    var animId = null;

    function hex2rgb(hex) {
      var n = parseInt(hex.replace('#', ''), 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    function lerp(a, b, t) { return a + (b - a) * t; }
    function lerpColor(c1, c2, t) {
      var a = hex2rgb(c1), b = hex2rgb(c2);
      return 'rgb(' + Math.round(lerp(a[0], b[0], t)) + ',' +
                     Math.round(lerp(a[1], b[1], t)) + ',' +
                     Math.round(lerp(a[2], b[2], t)) + ')';
    }

    function apply(p) {
      var w = ((p % COUNT) + COUNT) % COUNT;
      var i = Math.floor(w);
      var n = (i + 1) % COUNT;
      var t = w - i;

      for (var k = 0; k < imgs.length; k++) {
        var op = 0;
        if (k === i) op = 1 - t;
        else if (k === n) op = t;
        imgs[k].style.opacity = op.toFixed(3);
      }

      var nearest = Math.round(w) % COUNT;
      for (var l = 0; l < links.length; l++) {
        links[l].setAttribute('data-active', l === nearest ? 'true' : 'false');
      }

      var color = lerpColor(COLORS[i], COLORS[n], t);
      hero.style.setProperty('--hero-accent', color);

      for (var p2 = 0; p2 < pills.length; p2++) {
        pills[p2].setAttribute('aria-current', p2 === nearest ? 'true' : 'false');
      }

      if (priceEl && links[nearest]) {
        var newPrice = links[nearest].getAttribute('data-price');
        if (newPrice && priceEl.textContent !== newPrice) {
          priceEl.textContent = newPrice;
        }
      }
    }

    function animateTo(target, duration, onComplete) {
      if (animId) cancelAnimationFrame(animId);
      var start = current;
      var startTime = performance.now();
      duration = duration || 600;
      function tick(now) {
        var t = Math.min(1, (now - startTime) / duration);
        var eased = 1 - Math.pow(1 - t, 3);
        current = lerp(start, target, eased);
        apply(current);
        if (t < 1) animId = requestAnimationFrame(tick);
        else { current = target; apply(current); if (onComplete) onComplete(); }
      }
      animId = requestAnimationFrame(tick);
    }

    function snap() {
      var target = Math.round(current);
      animateTo(target, 420);
    }

    function fadeHint() {
      if (hint && !hint.classList.contains('is-faded')) hint.classList.add('is-faded');
    }

    /* -------- Auto-rotate loop -------- */
    var AUTO_INTERVAL = 1500;
    var AUTO_DURATION = 700;
    var autoTimer = null;
    var stoppedByUser = false;
    var prefersReducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function autoTick() {
      var target = Math.round(current) + 1;
      animateTo(target, AUTO_DURATION, function () {
        current = ((target % COUNT) + COUNT) % COUNT;
      });
    }
    function startAutoLoop() {
      if (stoppedByUser || prefersReducedMotion) return;
      if (autoTimer) return;
      autoTimer = setInterval(autoTick, AUTO_INTERVAL);
    }
    function pauseAutoLoop() {
      if (autoTimer) { clearInterval(autoTimer); autoTimer = null; }
    }
    function stopAutoLoopForGood() {
      stoppedByUser = true;
      pauseAutoLoop();
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) pauseAutoLoop();
      else startAutoLoop();
    });

    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      // Pause (don't stop forever) while the user holds the pouch — if
      // we let autoTick fire mid-drag it would yank the pouch out of
      // their hand. The loop resumes from release().
      pauseAutoLoop();
      dragging = true;
      moved = 0;
      captured = false;
      startX = e.clientX;
      startProgress = current;
      if (animId) cancelAnimationFrame(animId);
      // Do NOT setPointerCapture / add `is-dragging` here — that happens only
      // once the user actually drags (see pointermove). Capturing on a plain
      // click makes a mouse retarget the click to the stage, so the product
      // <a> never navigates (desktop-only bug; touch is unaffected).
      fadeHint();
      e.preventDefault();
    });

    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      moved = Math.abs(dx);
      // Engage the drag (pointer capture + class) only past the threshold so
      // a tap/click stays a real click and the product link can navigate.
      if (!captured && moved > 8) {
        captured = true;
        stage.classList.add('is-dragging');
        try { stage.setPointerCapture(e.pointerId); } catch (err) {}
      }
      var w = stage.offsetWidth || 400;
      var delta = -dx / (w * 0.55);
      current = startProgress + delta;
      apply(current);
    });

    function release() {
      if (!dragging) return;
      dragging = false;
      captured = false;
      stage.classList.remove('is-dragging');
      snap();
      // Drag released without a pill click — resume the carousel.
      // (If a pill click happens later, stoppedByUser will gate this off.)
      startAutoLoop();
    }
    stage.addEventListener('pointerup',     release);
    stage.addEventListener('pointercancel', release);
    stage.addEventListener('pointerleave',  release);

    stage.addEventListener('click', function (e) {
      if (moved > 8) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    Array.prototype.forEach.call(pills, function (pill) {
      pill.addEventListener('click', function () {
        var idx = parseInt(pill.getAttribute('data-index'), 10);
        if (isNaN(idx)) return;
        // Pill click = the user picked a flavor. End the auto-rotate
        // for the rest of the session.
        stopAutoLoopForGood();
        animateTo(idx, 650);
        fadeHint();
      });
    });

    /* "מחירים" button — smooth-scroll to the pricing clock. Prefer the site's
       Lenis instance (AnandaScroll.lenis) so it matches the site's scroll feel;
       fall back to native smooth scroll on touch / reduced-motion (Lenis is null
       there). Resolved at CLICK time so AnandaScroll is guaranteed loaded. The
       <a href="#ananda-pricing"> stays as the no-JS fallback. */
    var pricesBtn = hero.querySelector('[data-ananda-hero-prices]');
    if (pricesBtn) {
      pricesBtn.addEventListener('click', function (e) {
        var target = document.querySelector('.ananda-pricing');
        if (!target) return; // no pricing section here — let the href fall through
        e.preventDefault();
        var AS = window.AnandaScroll;
        if (AS && AS.lenis && typeof AS.lenis.scrollTo === 'function') {
          AS.lenis.scrollTo(target, { offset: -80, duration: 1.2 });
        } else {
          try {
            target.scrollIntoView({ behavior: (AS && AS.prefersReducedMotion) ? 'auto' : 'smooth', block: 'start' });
          } catch (err) { target.scrollIntoView(); }
        }
      });
    }

    apply(0);
    startAutoLoop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
